<template>
  <div class="border-2 px-3 py-4 radius-20 border-spect-gray overflow-x-auto">
    <DataTable
      :value="isLoading ? usersSkeleton : users"
      :stripedRows="true"
      :paginator="true"
      :rows="pageSize"
      :rowsPerPageOptions="[20, 50, 100, 150, 200]"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      currentPageReportTemplate="{first} à {last} sur {totalRecords}"
      responsiveLayout="stack"
      breakpoint="960px"
      :selection="selectedUsers"
      @update:selection="$emit('update:selectedUsers', $event)"
      dataKey="id"
      class="p-datatable-sm"
      :filters="filters"
      @update:filters="$emit('update:filters', $event)"
      :globalFilterFields="['firstname', 'lastname', 'email']"
    >
      <!-- Selection Column -->
      <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
      
      <!-- Full Name Column -->
      <Column :header="$t('user_manage.full_name')" sortable class="no-whitespace">
        <template #body="slotProps">
          <div v-if="isLoading" class="flex items-center gap-2">
            <Skeleton shape="circle" size="2rem"></Skeleton>
            <Skeleton width="8rem" height="1rem"></Skeleton>
          </div>
          <div v-else class="flex items-center gap-2">
            <Avatar 
              :image="slotProps.data.avatar" 
              :label="slotProps.data.firstname?.charAt(0) + slotProps.data.lastname?.charAt(0)"
              shape="circle" 
              size="normal"
            />
            <span class="no-whitespace">{{ slotProps.data.firstname }} {{ slotProps.data.lastname }}</span>
          </div>
        </template>
      </Column>

      <!-- Email Column -->
      <Column field="email" :header="$t('user_manage.email_label')" sortable>
        <template #body="slotProps">
          <Skeleton v-if="isLoading" width="10rem" height="1rem"></Skeleton>
          <span v-else>{{ slotProps.data.email }}</span>
        </template>
      </Column>

      <!-- Service Column -->
      <Column :header="$t('user_manage.service')" sortable>
        <template #body="slotProps">
          <Skeleton v-if="isLoading" width="6rem" height="1rem"></Skeleton>
          <span v-else>{{ getServiceName(slotProps.data.service?.name) || '...' }}</span>
        </template>
      </Column>

      <!-- Phone Column -->
      <Column :header="$t('user_manage.phone_label')" sortable>
        <template #body="slotProps">
          <Skeleton v-if="isLoading" width="8rem" height="1rem"></Skeleton>
          <span v-else>{{ slotProps.data.phone || '...' }}</span>
        </template>
      </Column>

      <!-- Status Column -->
      <Column :header="$t('user_manage.status')" sortable>
        <template #body="slotProps">
          <Skeleton v-if="isLoading" width="5rem" height="1rem"></Skeleton>
          <Tag v-else :value="getUserStatus(slotProps.data.role)" :severity="getStatusSeverity(slotProps.data?.role?.name)">
            {{ getUserStatus(slotProps.data.role) }}
          </Tag>
        </template>
      </Column>

      <!-- Actions Column -->
      <Column :header="$t('user_manage.actions')">
        <template #body="slotProps">
          <div v-if="isLoading" class="flex gap-2">
            <Skeleton width="2rem" height="2rem"></Skeleton>
            <Skeleton width="2rem" height="2rem"></Skeleton>
            <Skeleton width="2rem" height="2rem"></Skeleton>
            <Skeleton width="2rem" height="2rem"></Skeleton>
          </div>
          <div v-else class="flex gap-2">
            <Button 
              icon="pi pi-pencil" 
              size="small" 
              severity="info" 
              @click="$emit('edit-user', slotProps.data)"
              v-tooltip.top="$t('user_manage.edit')"
              v-if="slotProps.data?.role?.name !== 'manager'"
            />
            <Button 
              v-if="slotProps.data.is_active && slotProps.data?.role?.name !== 'manager'"
              icon="pi pi-user-edit" 
              size="small" 
              severity="warn" 
              @click="$emit('toggle-status', slotProps.data)" 
              v-tooltip.top="$t('user_manage.deactivate')"
            />
            <Button 
              v-if="!slotProps.data.is_active && slotProps.data?.role?.name !== 'manager'"
              icon="pi pi-user-edit" 
              size="small" 
              severity="success" 
              @click="$emit('toggle-status', slotProps.data)" 
              v-tooltip.top="$t('user_manage.activate')"
            />
            <Button 
              v-if="!slotProps.data.is_active && !slotProps.data.email_verified_at && slotProps.data?.role?.name !== 'manager'"
              icon="pi pi-send" 
              size="small" 
              severity="secondary" 
              @click="$emit('resend-invitation', slotProps.data)" 
              v-tooltip.top="$t('user_manage.resend_activation')"
            />
            <Button
              :icon="slotProps.data?.role?.name === 'manager' ? 'pi pi-lock-open' : 'pi pi-key'" 
              size="small" 
              variant="outlined"
              :severity="slotProps.data?.role?.name === 'manager' ? 'warn' : 'success'" 
              @click="$emit('manager-access', slotProps.data)"
              v-tooltip.top="slotProps.data?.role?.name === 'manager' ? 'Révoquer accès' : 'Accès manager'"
            />
            <Button
              v-if="slotProps.data?.role?.name !== 'manager'" 
              icon="pi pi-trash" 
              size="small" 
              severity="danger" 
              @click="$emit('delete-user', slotProps.data)"
              v-tooltip.top="$t('user_manage.delete')"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import Avatar from 'primevue/avatar'
import Skeleton from 'primevue/skeleton'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

defineProps({
  users: Array,
  selectedUsers: Array,
  isLoading: Boolean,
  filters: Object,
  pageSize: {
    type: Number,
    default: 50
  }
})

defineEmits([
  'edit-user',
  'manager-access',
  'delete-user', 
  'toggle-status',  // Changed from 'change-status'
  'resend-invitation',  // Changed from 'resend-activation'
  'update:selectedUsers',
  'update:filters'
])

// Create skeleton data for loading state
const usersSkeleton = computed(() => {
  return Array.from({ length: 5 }, (_, index) => ({
    id: index,
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    service: '',
    status: ''
  }))
})

const getUserStatus = (role) => {
  if (role.name == "manager") {
    return t("user_manage.table_manager")
  }
  return t("user_manage.table_member")
}

const getServiceName = (serviceName) => {
  if (typeof serviceName !== 'string' || !serviceName) return ''
  return serviceName.split("_")[0]
}

// Helper function to determine status severity
const getStatusSeverity = (status) => {
  switch (status?.toLowerCase()) {
    case 'manager':
      return 'info'
    case 'active':
      return 'success'
    case 'pending':
      return 'warning'
    case 'inactive':
    case 'disabled':
      return 'danger'
    default:
      return 'info'
  }
}
</script>

<style lang="scss" scoped>
@use "../../assets/scss/variables.scss";
@use "../../assets/scss/app/common.scss";
</style>